/**
 * Customer-facing page that consumes the single-use confirmation
 * token coming from the email link.
 *
 * Two actions supported:
 *  - confirm (default): POST /api/customers/erasure/confirm/{token}
 *  - revoke (action=revoke query): POST /api/customers/erasure/revoke/{token}
 *
 * URL patterns:
 *   /erasure/confirm?token=...&cid=...                → confirm
 *   /erasure/confirm?token=...&cid=...&action=revoke  → revoke (the
 *     "ho cambiato idea" link from the confirmation email)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, ShieldCheck, Undo2 } from "lucide-react";
import { API } from "../config";

const CATEGORY_LABEL_IT = {
  marketing: "Dati marketing",
  profiling: "Profilazione e preferenze",
  account: "Account anagrafico",
  tracking: "Tracciati GPS (anonimizzati)",
};

const ErasureConfirmPage = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const action = params.get("action"); // null = confirm, "revoke" = cancel

  const [state, setState] = useState({ loading: true, error: null, result: null, mode: action === "revoke" ? "revoke" : "confirm" });

  useEffect(() => {
    if (!token) {
      setState((s) => ({ ...s, loading: false, error: "Token mancante", result: null }));
      return;
    }
    (async () => {
      const isRevoke = action === "revoke";
      const url = isRevoke
        ? `${API}/customers/erasure/revoke/${token}`
        : `${API}/customers/erasure/confirm/${token}`;
      try {
        const r = await axios.post(url);
        setState({ loading: false, error: null, result: r.data, mode: isRevoke ? "revoke" : "confirm" });
      } catch (e) {
        setState({
          loading: false,
          error: e.response?.data?.detail || "Operazione non riuscita",
          result: null,
          mode: isRevoke ? "revoke" : "confirm",
        });
      }
    })();
  }, [token, action]);

  const isRevoke = state.mode === "revoke";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-12" data-testid="erasure-confirm-page">
      <div className="max-w-md w-full">
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-7">
          <div className="flex items-center gap-3 mb-5">
            {isRevoke
              ? <Undo2 size={20} className="text-lojack-clause-400" />
              : <ShieldCheck size={20} className="text-lojack-blue-400" />}
            <div>
              <h1 className="text-lg font-bold text-white">
                {isRevoke ? "Annulla richiesta" : "Cancellazione dati"}
              </h1>
              <p className="text-gray-500 text-xs">GDPR Art. 17 · {isRevoke ? "Ripensamento" : "Conferma"}</p>
            </div>
          </div>

          {state.loading && (
            <div className="py-8 text-center">
              <div className="spinner mx-auto" />
              <p className="text-gray-500 text-sm mt-4">
                {isRevoke ? "Annullamento in corso…" : "Esecuzione in corso…"}
              </p>
            </div>
          )}

          {state.error && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 mt-2" data-testid="erasure-error">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-red-300 font-semibold text-sm">
                    {isRevoke ? "Annullamento non riuscito" : "Conferma non riuscita"}
                  </p>
                  <p className="text-red-200/80 text-xs mt-1.5 leading-relaxed">{state.error}</p>
                  <p className="text-gray-500 text-[11px] mt-3 leading-relaxed">
                    Possibili cause: link scaduto (oltre 24 ore), token già usato o già annullato, oppure una nuova controversia è stata aperta nel frattempo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {state.result && isRevoke && (
            <div className="space-y-3" data-testid="erasure-revoked">
              <div className="bg-lojack-clause-500/10 border border-lojack-clause-500/40 rounded-xl p-4 flex items-start gap-3">
                <Undo2 size={18} className="text-lojack-clause-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-lojack-clause-300 font-semibold text-sm">Richiesta annullata</p>
                  <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                    La tua richiesta di cancellazione dati è stata annullata. Nessun dato è stato modificato. Se vuoi puoi riavviare il flusso dal Wallet in qualsiasi momento.
                  </p>
                </div>
              </div>
              {state.result.time_to_revoke_seconds != null && (
                <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-3 text-xs text-gray-500">
                  Tempo di ripensamento registrato: <strong className="text-gray-300">{Math.round(state.result.time_to_revoke_seconds)} secondi</strong>
                </div>
              )}
            </div>
          )}

          {state.result && !isRevoke && (
            <div className="space-y-3" data-testid="erasure-success">
              <div className="bg-lojack-blue-500/10 border border-lojack-blue-500/40 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-lojack-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-lojack-blue-300 font-semibold text-sm">Cancellazione completata</p>
                  <p className="text-gray-400 text-xs mt-1 leading-relaxed">Ti abbiamo inviato una ricevuta via email come prova della tua richiesta esercitata ai sensi dell'art. 17 del GDPR.</p>
                </div>
              </div>

              <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4">
                <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">Categorie</p>
                <ul className="space-y-2">
                  {Object.entries(state.result.results || {}).map(([cat, outcome]) => (
                    <li key={cat} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{CATEGORY_LABEL_IT[cat] || cat}</span>
                      <span className={outcome.status === "ok" ? "text-lojack-blue-400 font-semibold text-xs" : "text-red-400 font-semibold text-xs"}>
                        {outcome.status === "ok" ? "Completata" : "Errore"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <p className="text-gray-600 text-[10px] text-center mt-6 leading-relaxed">
          LoJack Digital Wallet · GDPR Art. 17 par. 1 → 3 · Conservazione obblighi di legge: art. 2220 c.c.
        </p>
      </div>
    </div>
  );
};

export default ErasureConfirmPage;
